/**
 * "Client Management" is not something auth-service sends — it is the `user_management`
 * navigation entry relabelled for admins.
 *
 * Organization Management, by contrast, is its own entry (`text: "organizations"`) that
 * auth-service serves directly per MP-3382, gated on a per-user `organization_service`
 * grant — it is no longer synthesized here. Access is: the entry is present in the
 * `navigation` claim (i.e. the grant is live), AND the ORG_MANAGEMENT_FLAG LaunchDarkly
 * flag is on. The flag is an environment-level kill switch now, not a per-user gate —
 * the grant is the source of truth for who sees the tab.
 *
 * `canAccessOrgManagement` is the single home for that rule; `decorateNav` below and
 * `middleware/admin.global.js` both defer to it rather than restating it.
 */

const USER_MANAGEMENT_KEY = 'user_management';
const CLIENT_MANAGEMENT_KEY = 'client_management';

/**
 * Doubles as the sidebar label: the nav renders `text` with underscores swapped for
 * spaces, uppercased. Kept short because both sidebars set `white-space: nowrap` on
 * the label and clip anything much past ~20 characters — "organization_management"
 * rendered as a truncated "ORGANIZATION MANAGEMEN…". Also matches the /organizations route.
 */
export const ORG_MANAGEMENT_KEY = 'organizations';

/**
 * LaunchDarkly kill-switch. The dashboard key is `organization-management-enabled`;
 * vue-ld lodash-camelCases flag keys, so this is what `$ld.flags` exposes. Absent or
 * off hides the nav tab AND blocks the route, even for a user whose grant is live.
 *
 * Target by user key, not by client_id: an admin's LD context client_id is
 * `userProfile.client || accessible_clients[0].client.id` — arbitrary for internal
 * admins, and swapped out by `$ld.reinitialize` on client switch (GeneralInfo.vue).
 * A client-targeted rule would make the tab flicker as an admin moves between clients.
 */
export const ORG_MANAGEMENT_FLAG = 'organizationManagementEnabled';

interface NavigationEntry {
  text: string;
  img_src?: string | null;
  [key: string]: unknown;
}

interface NavUserProfile {
  roles?: Record<string, string>;
  navigation?: NavigationEntry[];
}

/**
 * Whether this user has the Client Management tab. Pure so route middleware can call
 * it without Nuxt context.
 */
export const hasClientManagement = (userProfile?: NavUserProfile | null): boolean => {
  if (userProfile?.roles?.user_management !== 'admin') return false;
  return Boolean(userProfile?.navigation?.some((entry) => entry.text === USER_MANAGEMENT_KEY));
};

/**
 * Whether auth-service has granted this user the organizations nav entry — i.e. they
 * hold a live `organization_service` grant (MP-3382). This is the per-user source of
 * truth; there is nothing left to derive client-side.
 */
export const hasOrgManagementEntry = (userProfile?: NavUserProfile | null): boolean =>
  Boolean(userProfile?.navigation?.some((entry) => entry.text === ORG_MANAGEMENT_KEY));

/**
 * The whole Organization Management access rule, in one expression, shared by the route
 * guard and the nav so the two cannot disagree. Pure — `flags` is passed in so route
 * middleware can call it without Nuxt context.
 */
export const canAccessOrgManagement = (
  userProfile?: NavUserProfile | null,
  flags?: Record<string, unknown> | null,
): boolean => hasOrgManagementEntry(userProfile) && Boolean(flags?.[ORG_MANAGEMENT_FLAG]);

// provideFlags declares no type for `$ld`; `flags` is the only part read here. Its identity is
// stable for the app's lifetime (`reinitialize` mutates it in place), so it is safe to capture.
export const ldClient = () => (useNuxtApp() as unknown as { $ld?: { flags?: Record<string, unknown> } }).$ld;

export const useClientManagementNav = () => {
  // Captured in setup scope; `$ld` identity is stable for the app's lifetime
  // (`reinitialize` mutates it in place). Reads inside `decorateNav` are still tracked
  // by the navbars' computeds — Vue dependency tracking is dynamic-scope, not lexical.
  const $ld = ldClient();

  /**
   * Returns a new navigation array — never mutates the profile.
   *
   * The organizations entry itself now comes from auth-service (MP-3382) — its url,
   * api_url and icon are whatever the backend sends, at whatever position the backend
   * sorts it to (position 8, after app_manager/theme_manager — no longer spliced
   * directly under Client Management). This function's only remaining jobs are the
   * user_management -> client_management relabel, and filtering the organizations entry
   * out when ORG_MANAGEMENT_FLAG is off — the flag is an environment-level switch, and
   * auth-service has no notion of it.
   */
  const decorateNav = (userProfile?: NavUserProfile | null): NavigationEntry[] => {
    const apps: NavigationEntry[] = JSON.parse(JSON.stringify(userProfile?.navigation || []));

    const orgEnabled = canAccessOrgManagement(userProfile, $ld?.flags);
    const filtered = orgEnabled ? apps : apps.filter((entry) => entry.text !== ORG_MANAGEMENT_KEY);

    if (!hasClientManagement(userProfile)) return filtered;

    // Guaranteed present by hasClientManagement; this narrows the type.
    const userManagement = filtered.find((entry) => entry.text === USER_MANAGEMENT_KEY);
    if (!userManagement) return filtered;

    userManagement.text = CLIENT_MANAGEMENT_KEY;
    userManagement.img_src =
      userManagement.img_src?.replace(USER_MANAGEMENT_KEY, CLIENT_MANAGEMENT_KEY) ?? userManagement.img_src;

    return filtered;
  };

  return { decorateNav };
};
